import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, InputLabel, MenuItem, Select, TextField, Typography, CircularProgress,
} from '@mui/material';
import { requirementsApi } from '../../services/api';

/**
 * #678 v2 — explicit verification EVIDENCE records (method / result / evidence /
 * actor), layered on top of v1's derived, trace-only tri-state (#582). Modest by
 * design, mirroring `ApprovalReasonDialog`'s shape (a single focused Dialog, not a
 * new page/tab): existing records + staleness on top, a short add-record form below.
 *
 * v1 already answers "does a test_case link touch this item"; this is the missing
 * evidence dimension for analysis/inspection/demonstration — a `test_case` link is the
 * only proof v1's data model can carry. Recording evidence here immediately widens the
 * item's `effective_state` (the badge on the item header) even with no trace link at
 * all, because the rollup is EITHER dimension, not both required.
 *
 * Records are APPEND-ONLY (mirrors the backend: no edit/delete here) — every call adds
 * a fresh row, so a repeat test run or a different method is its own record, not an
 * overwrite of the last one.
 */
const METHODS = ['test', 'analysis', 'inspection', 'demonstration'];
const RESULTS = ['pass', 'fail'];

function methodLabel(m) {
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : m;
}

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function VerificationRecordDialog({ open, itemId, itemTitle, onClose, onRecorded }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [method, setMethod] = useState('test');
  const [result, setResult] = useState('pass');
  const [evidence, setEvidence] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const load = React.useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await requirementsApi.getVerificationRecords(itemId);
      setRecords(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setLoadError(err.message || 'Failed to load verification records');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (open) {
      setMethod('test');
      setResult('pass');
      setEvidence('');
      setSubmitError(null);
      load();
    }
  }, [open, load]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await requirementsApi.addVerificationRecord(itemId, { method, result, evidence });
      setEvidence('');
      await load();
      onRecorded?.();
    } catch (err) {
      setSubmitError(
        (err.response && err.response.data && err.response.data.detail)
        || err.message || 'Failed to record verification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
           data-testid="verification-record-dialog">
      <DialogTitle>Verification evidence{itemTitle ? ` — ${itemTitle}` : ''}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading records…</Typography>
          </Box>
        )}
        {loadError && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>{loadError}</Typography>
        )}
        {!loading && !loadError && records.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No verification evidence recorded yet.
          </Typography>
        )}
        {!loading && records.length > 0 && (
          <Box data-testid="verification-record-list" sx={{ mb: 2 }}>
            {records.map((r) => (
              <Box
                key={r.record_id}
                data-testid="verification-record-row"
                sx={{
                  py: 0.75, borderBottom: '1px solid', borderColor: 'divider',
                  display: 'flex', flexDirection: 'column', gap: 0.25,
                }}
              >
                <Typography variant="body2">
                  <strong>{methodLabel(r.method)}</strong>
                  {': '}
                  {r.result === 'pass' ? 'Pass' : 'Fail'}
                  {r.evidence_ref ? ` — ${r.evidence_ref}` : ''}
                  {r.stale && (
                    <span
                      data-testid="verification-record-stale"
                      style={{ marginLeft: 8, color: '#a15c00', fontWeight: 600 }}
                    >
                      STALE
                    </span>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(r.actor || 'unknown')}{r.created_at ? ` · ${fmtWhen(r.created_at)}` : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Record new evidence</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="verification-method-label">Method</InputLabel>
            <Select
              labelId="verification-method-label"
              label="Method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              data-testid="verification-method-select"
            >
              {METHODS.map((m) => (
                <MenuItem key={m} value={m}>{methodLabel(m)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="verification-result-label">Result</InputLabel>
            <Select
              labelId="verification-result-label"
              label="Result"
              value={result}
              onChange={(e) => setResult(e.target.value)}
              data-testid="verification-result-select"
            >
              {RESULTS.map((r) => (
                <MenuItem key={r} value={r}>{r === 'pass' ? 'Pass' : 'Fail'}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          fullWidth
          size="small"
          label="Evidence (optional)"
          placeholder="A verifying test_case id, a run/report reference, or an attachment ref"
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'verification-evidence-input' } }}
        />
        {submitError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>{submitError}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} data-testid="verification-record-close">Close</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting}
          data-testid="verification-record-submit"
        >
          {submitting ? 'Recording…' : 'Record evidence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

VerificationRecordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  itemId: PropTypes.string,
  itemTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRecorded: PropTypes.func,
};

export default VerificationRecordDialog;
